/**
 * useDisputeWorkflow Hook
 * Architecture v1 - Frontend Wiring
 * 
 * Provides access to dispute workflow functionality:
 * - Reason library
 * - Routing strategies
 * - Template families
 * - Prompt keys
 * - Workflow resolution
 */

import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  REASON_LIBRARY,
  ROUTING_STRATEGIES,
  TEMPLATE_FAMILIES,
  PROMPT_KEYS,
  getAllReasons,
  getReasonByKey,
  getReasonsByCategory,
  findPromptKeysForReason,
} from '../config';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hook for managing dispute workflow state and API calls
 */
export const useDisputeWorkflow = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentWorkflow, setCurrentWorkflow] = useState(null);

  // Get all available reasons
  const allReasons = useMemo(() => getAllReasons(), []);

  // Get reason categories
  const categories = useMemo(() => Object.keys(REASON_LIBRARY), []);

  // Get reasons by severity
  const reasonsBySeverity = useMemo(() => {
    const bySeverity = { high: [], medium: [], low: [] };
    allReasons.forEach(reason => {
      const severity = reason.severity || 'medium';
      if (bySeverity[severity]) {
        bySeverity[severity].push(reason);
      }
    });
    return bySeverity;
  }, [allReasons]);

  // Get reasons by classification
  const reasonsByClassification = useMemo(() => {
    const byClass = { factual: [], legal: [], escalation: [] };
    allReasons.forEach(reason => {
      const classification = reason.classification || 'factual';
      if (byClass[classification]) {
        byClass[classification].push(reason);
      }
    });
    return byClass;
  }, [allReasons]);

  // Fetch applicable reasons for an account from backend
  const fetchApplicableReasons = useCallback(async (account, token) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API}/dispute-engine/applicable-reasons`,
        { account },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch applicable reasons');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Resolve workflow from selected reasons
  const resolveWorkflow = useCallback(async (account, selectedReasons, disputeRound = 1, priorDisputeRef = null, token) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API}/dispute-engine/resolve-workflow`,
        {
          account,
          selected_reasons: selectedReasons,
          dispute_round: disputeRound,
          prior_dispute_ref: priorDisputeRef,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentWorkflow(response.data);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resolve workflow');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get instruction texts for selected reasons
  const fetchInstructionTexts = useCallback(async (reasonKeys, token) => {
    try {
      const response = await axios.post(
        `${API}/dispute-engine/instruction-texts`,
        { reason_keys: reasonKeys },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.instruction_texts;
    } catch (err) {
      console.error('Failed to fetch instruction texts:', err);
      return {};
    }
  }, []);

  // Local helpers that work without API calls
  const getLocalReasonsByCategory = useCallback((category) => {
    return getReasonsByCategory(category);
  }, []);

  const getLocalReasonByKey = useCallback((key) => {
    return getReasonByKey(key);
  }, []);

  const getLocalPromptKeysForReason = useCallback((reasonKey) => {
    return findPromptKeysForReason(reasonKey);
  }, []);

  // Get routing strategy details
  const getRoutingStrategy = useCallback((key) => {
    return ROUTING_STRATEGIES[key];
  }, []);

  // Get template family details
  const getTemplateFamily = useCallback((key) => {
    return TEMPLATE_FAMILIES[key];
  }, []);

  // Get prompt key details
  const getPromptKey = useCallback((key) => {
    return PROMPT_KEYS[key];
  }, []);

  // Clear current workflow
  const clearWorkflow = useCallback(() => {
    setCurrentWorkflow(null);
    setError(null);
  }, []);

  return {
    // State
    loading,
    error,
    currentWorkflow,

    // Data
    allReasons,
    categories,
    reasonsBySeverity,
    reasonsByClassification,
    routingStrategies: ROUTING_STRATEGIES,
    templateFamilies: TEMPLATE_FAMILIES,
    promptKeys: PROMPT_KEYS,

    // API methods
    fetchApplicableReasons,
    resolveWorkflow,
    fetchInstructionTexts,

    // Local helpers (no API call needed)
    getReasonsByCategory: getLocalReasonsByCategory,
    getReasonByKey: getLocalReasonByKey,
    getPromptKeysForReason: getLocalPromptKeysForReason,
    getRoutingStrategy,
    getTemplateFamily,
    getPromptKey,

    // Actions
    clearWorkflow,
  };
};

/**
 * Severity badge colors
 */
export const SEVERITY_COLORS = {
  high: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  low: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
};

/**
 * Classification badge colors
 */
export const CLASSIFICATION_COLORS = {
  factual: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' },
  legal: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  escalation: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
};

/**
 * Category icons mapping
 */
export const CATEGORY_ICONS = {
  identity: '👤',
  balance: '💰',
  dates: '📅',
  status: '📊',
  payment_history: '📈',
  creditor: '🏢',
  terms: '📝',
  collection: '📋',
  public_record: '⚖️',
  inquiry: '🔍',
  personal_info: '👥',
  compliance: '📜',
  consistency: '🔄',
  escalation: '⬆️',
};

export default useDisputeWorkflow;
